import { MouseEvent, ReactNode, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
// eslint-disable-next-line import/no-namespace
import * as SS from 'styled-system';

import { Hide } from 'components/Hide';
import { Icon } from 'components/Icon';
import { colors, shadows } from 'theme/theme';
import { ClickOutside } from 'utils/ClickOutside';

const navLinkCss = css<{ $blue?: boolean }>`
  font-size: inherit;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 0.25;
  }
  ${SS.fontWeight};

  color: ${(props) =>
    props.$blue ? colors.brandBlue : colors.mediumContentGrey};
`;

const NavLink = styled.a<{ $blue?: boolean }>`
  ${navLinkCss};
`;

const MenuList = styled.div<{
  $width?: number;
  $height?: string;
}>`
  max-width: 270px;
  min-width: 200px;
  overflow-x: hidden;

  ${(props) =>
    props.$width &&
    css`
      min-width: ${props.$width}px;
    `}
  ${(props) =>
    props.$height &&
    css`
      max-height: ${props.$height};
      overflow-y: scroll;
    `}
`;

const StyledDropdownMenuNav = styled.button<{
  $open: boolean;
  $right?: boolean;
  $blue?: boolean;
}>`
  z-index: 2100;
  display: flex;
  align-items: center;
  margin-left: 6px;
  margin-right: 6px;
  padding: 8px 12px;
  font-size: inherit;

  ${(props) =>
    props.$open &&
    css`
      border-radius: 4px;
      background-color: ${colors.calloutGrey};
    `}

  ${(props) =>
    props.$right &&
    css`
      justify-content: end;
    `};

  color: ${(props) =>
    props.$blue ? colors.brandBlue : colors.mediumContentGrey};

  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
`;

type HideOptions = {
  xs?: boolean;
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
  xl?: boolean;
};

const menuItemCss = css`
  display: block;
  margin: 0;
  padding: 10px 12px;
  cursor: pointer;
  color: ${colors.lightContentGrey};
  text-decoration: none;
  font-size: inherit;
  font-weight: normal;
  white-space: unset;

  &:hover {
    background-color: ${colors.inputGrey};
    text-decoration: none;
    color: ${colors.mediumContentGrey};
  }

  .truncate {
    overflow: hidden;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
`;

export const PageHeaderDropdownMenuItem = styled(NavLink)<{
  $disabled?: boolean;
  $logoutLink?: boolean;
}>`
  ${menuItemCss};

  ${(props) =>
    props.$logoutLink &&
    css`
      color: ${colors.decorativePoppy};

      &:hover {
        color: ${colors.decorativePoppy};
        background-color: ${colors.lightRed};
      }
    `}
`;

const StyledDropdownMenuContainer = styled.div<{
  $right?: boolean;
  $open?: boolean;
}>`
  position: absolute;
  z-index: 2000;
  overflow: hidden;
  box-shadow: ${shadows.highest};
  background-color: white;
  border-radius: 4px;
  margin-top: 4px;
  transition: all 0.166s;
  ${(props) =>
    props.$open
      ? `
    display: block;
  `
      : `
    display: none;
  `}

  ${(props) =>
    props.$right
      ? css`
          right: 0;
        `
      : css`
          left: 0;
        `};
`;
const StyledDropdownMenu = styled.div`
  position: relative;
`;

type Props = {
  title: ReactNode;
  width?: number;
  height?: string;
  right?: boolean;
  children:
    | ReactNode
    | ((data: { closeMenu: () => void; open: boolean }) => ReactNode);
  'data-qa-id': string;
  openQaId?: string;
  closeQaId?: string;
  dropdownQaId?: string;
  'aria-label'?: string;
  blue?: boolean;
  hideArrow?: HideOptions;
  onClick?: (open: boolean) => void;
};

export function StaffPageHeaderDropdownMenu({
  title,
  width,
  height,
  right,
  children,
  'data-qa-id': qaId,
  openQaId,
  closeQaId,
  dropdownQaId,
  'aria-label': ariaLabel,
  blue,
  hideArrow,
  onClick,
  ...props
}: Props) {
  const [open, setOpen] = useState<boolean>(false);

  const closeMenu = useCallback(() => setOpen(false), []);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      setOpen((isOpen) => !isOpen);

      if (onClick) {
        onClick(!open);
      }
    },
    [onClick, open],
  );

  return (
    <ClickOutside onClickOutside={() => open && closeMenu()}>
      <StyledDropdownMenu
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        <StyledDropdownMenuNav
          onClick={handleClick}
          data-qa-id={qaId ? `${qaId}-toggle` : undefined}
          aria-label={ariaLabel}
          $open={open}
          $right={right}
        >
          {title}
          {/* TODO: Fix this the next time the file is edited. */}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Hide {...hideArrow}>
            <div style={{ marginTop: -2, marginLeft: 5 }}>
              <Icon name={!open ? 'chevron-up' : 'chevron-down'} size={9} />
            </div>
          </Hide>
        </StyledDropdownMenuNav>

        <StyledDropdownMenuContainer
          $right={right}
          $open={open}
          data-qa-id={qaId}
        >
          <MenuList $width={width} $height={height}>
            {typeof children === 'function'
              ? children({ closeMenu, open })
              : children}
          </MenuList>
        </StyledDropdownMenuContainer>
      </StyledDropdownMenu>
    </ClickOutside>
  );
}

export const StaffPageHeaderDropdownMenuSection = styled.div<{
  $noBorder?: boolean;
}>`
  ${(p) =>
    p.$noBorder ? null : `border-top: solid 1px ${colors.selectionGrey};`}
  width: 100%;
`;

export const StaffPageHeaderDropdownMenuSectionTitle = styled.div`
  color: ${colors.lightContentGrey};
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 1.8px;
  white-space: nowrap;
  display: block;
  margin: 0;
  padding: 12px;
  background-color: ${colors.backgroundBlue};
`;
